/**
 * Single-Flight Request Deduplication
 *
 * Prevents duplicate concurrent requests to the same resource.
 * If a request is already in flight, returns the existing promise.
 */

// In-flight promises cache
const inFlightRequests = new Map<string, Promise<any>>();

/**
 * Wrap an async function to ensure only one request per key is in flight at a time
 *
 * @param key - Unique identifier for this request (e.g., "usage:user123")
 * @param fn - Async function to execute
 * @returns Promise that resolves with the result
 */
export async function singleFlight<T>(key: string, fn: () => Promise<T>): Promise<T> {
  // Check if request is already in flight
  if (inFlightRequests.has(key)) {
    return inFlightRequests.get(key) as Promise<T>;
  }

  // Start new request
  const promise = fn().finally(() => {
    // Clear from cache when complete (success or error)
    inFlightRequests.delete(key);
  });

  // Cache the promise
  inFlightRequests.set(key, promise);

  return promise;
}

/**
 * Clear a specific in-flight request (useful for forcing refetch)
 */
export function clearSingleFlight(key: string): void {
  inFlightRequests.delete(key);
}

/**
 * Clear all in-flight requests
 */
export function clearAllSingleFlight(): void {
  inFlightRequests.clear();
}
